import { useColorMode, useDisclosure, VStack } from '@chakra-ui/react'
import { Body } from '@missionlabs/react'
import {
    ExtensionMenu,
    ExtensionTeam,
    ExtensionUser,
    isUser,
    IVRMenu,
    UserOrTeam
} from '@missionlabs/types'
import { inferFirstLastName, isMenu, isTeam, sort } from '@missionlabs/utils'
import { UTMDrawer } from 'features/settings/components/UTMDrawer'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isPhoneLinePlus } from 'shared/utils/env'

import { buildIVROptionsArray } from '../utils/ivr'
import { IVRMenuOption } from './IVRMenuOption'
import { NewIVRMenuOption } from './NewIVRMenuOption'

interface IVRMenuOptionsProps {
    extensions: IVRMenu['IVR']['extensions']
    onUpdateExtensions: (extensions: IVRMenu['IVR']['extensions']) => void
}

type UserTeamOrMenu = ExtensionUser | ExtensionTeam | ExtensionMenu
type EditingKeyState = { index: string; data: UserTeamOrMenu }

const { MIN_KEY, MAX_KEY, ALLOW_SYMBOLS } = isPhoneLinePlus
    ? {
          MIN_KEY: 0,
          MAX_KEY: 9,
          ALLOW_SYMBOLS: true
      }
    : {
          MIN_KEY: 0,
          MAX_KEY: 6,
          ALLOW_SYMBOLS: false
      }

export function IVRMenuOptions({ extensions = {}, onUpdateExtensions }: IVRMenuOptionsProps) {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const utmDrawer = useDisclosure()

    // Which is the current key being edited, and what is its existing value?
    const [editingKey, setEditingKey] = useState<EditingKeyState | undefined>()

    const menuOptions = useMemo(() => {
        const ext = Object.entries(extensions)
        const options = ext.filter(ex => Number(ex[0]) <= MAX_KEY).sort(([a], [b]) => sort(a, b))

        if (ALLOW_SYMBOLS)
            return options.concat(
                ext.filter(ex => isNaN(Number(ex[0]))).sort(([a], [b]) => sort(a, b))
            )

        return options
    }, [extensions])
    // The keys that are not currently in use
    const availableKeys = useCallback(
        (currentKey?: string): string[] => getAvailableKeys(extensions, currentKey),
        [extensions]
    )

    const updateKey = useCallback(
        (index: string, data: UserOrTeam) => {
            const updatedExtensions = { ...extensions }
            if (isUser(data)) {
                const { fullName } = inferFirstLastName(data)
                updatedExtensions[index] = { userID: data.ID, display: fullName }
            }
            if (isMenu(data)) updatedExtensions[index] = { menuID: data.ID, display: data.name }
            if (isTeam(data)) updatedExtensions[index] = { teamID: data.ID, display: data.name }
            onUpdateExtensions(updatedExtensions)
        },
        [extensions, onUpdateExtensions]
    )

    // Change a key's index from one to another
    const swapKeyIndex = useCallback(
        (currentIndex: string, newIndex: string) => {
            const changedExtensions = { ...extensions }
            changedExtensions[newIndex] = changedExtensions[currentIndex]
            delete changedExtensions[currentIndex]
            onUpdateExtensions(changedExtensions)
        },
        [extensions, onUpdateExtensions]
    )

    const deleteKey = useCallback(
        (index: string) => {
            const changedExtensions = { ...extensions }
            delete changedExtensions[index]
            onUpdateExtensions(changedExtensions)
        },
        [extensions, onUpdateExtensions]
    )

    const startEditingKey = useCallback(
        (index: string, data: UserTeamOrMenu) => {
            setEditingKey({ index, data })
            utmDrawer.onOpen()
        },
        [utmDrawer]
    )

    const stopEditingKey = useCallback(() => {
        setEditingKey(undefined)
        utmDrawer.onClose()
    }, [utmDrawer])

    return (
        <VStack align="flex-start" gap={6}>
            <VStack align="flex-start" gap={2}>
                <Body variant="bold">{t('admin.menus.optionsTitle')}</Body>
                <Body size="sm" variant="bold" color={`${colorMode}.tones.stormGrey`}>
                    {t('admin.menus.optionsSubtitle', { min: MIN_KEY, max: MAX_KEY })}
                </Body>
            </VStack>

            <VStack align="flex-start" alignSelf="stretch" gap={4}>
                {menuOptions.map(([index, value]) => (
                    <IVRMenuOption
                        key={index}
                        index={index}
                        display={value.display}
                        availableKeys={availableKeys(index)}
                        onSwap={newIndex => swapKeyIndex(index, newIndex)}
                        onEdit={() => startEditingKey(index, value as UserTeamOrMenu)}
                        onDelete={() => deleteKey(index)}
                    />
                ))}

                <NewIVRMenuOption availableKeys={availableKeys()} onSubmit={updateKey} />
            </VStack>

            {/* User, teams and menus drawer used to select which destination to point a menu option to */}
            <UTMDrawer
                isOpen={utmDrawer.isOpen}
                onClose={stopEditingKey}
                defaultValue={
                    (editingKey?.data as ExtensionUser)?.userID ??
                    (editingKey?.data as ExtensionTeam)?.teamID ??
                    (editingKey?.data as ExtensionMenu)?.menuID
                }
                onSubmit={data => {
                    if (!editingKey) return
                    updateKey(editingKey.index, data)
                    utmDrawer.onClose()
                }}
            />
        </VStack>
    )
}

export function getAvailableKeys(
    extensions: IVRMenu['IVR']['extensions'] = {},
    currentKey?: string
): string[] {
    const usedKeyPositions = Object.keys(extensions)
    const ASSIGNABLE_KEYS = buildIVROptionsArray(MIN_KEY, MAX_KEY, ALLOW_SYMBOLS)
    return ASSIGNABLE_KEYS.filter(key => {
        const isCurrentKey = key === currentKey
        const isExistingKey = usedKeyPositions.some(existingKey => existingKey === key)
        return isCurrentKey || !isExistingKey
    })
}
